/**
 * SfxGenerationPanel Component
 *
 * Main panel combining all sub-components for sound effect generation.
 * Generation lifecycle is delegated to useSfxGeneration (GenerationContext)
 * so that progress and result survive tab switches and page reloads.
 */

import { useState, useEffect, useCallback, useMemo, useRef, memo } from 'react';
import { Sparkles, Play, AlertCircle, CheckCircle, Music, Repeat } from 'lucide-react';
import { useTranslation } from '../../hooks/useTranslation';
import { isDesktopApp } from '../../utils/apiMode';
import { SfxProviderSelector } from './SfxProviderSelector';
import { SfxPromptInput } from './SfxPromptInput';
import { SfxDurationSlider } from './SfxDurationSlider';
import { SfxVideoUpload } from './SfxVideoUpload';
import {
  LocalModelInstallGate,
  LOCAL_MODEL_GATE_PRIMARY_BUTTON_CLASS,
} from '../common/LocalModelInstallGate';
import { type SfxProvider } from './SfxProviderSelector.constants';
import { useLocalModelInstalls } from '../../hooks/useLocalModelInstalls';
import { useSfxGeneration } from '../../hooks/useSfxGeneration';
import { useResultDownload } from '../../hooks/useResultDownload';
import { ResultMedia } from '../common/ResultMedia';
import {
  SFX_PROVIDERS,
  SFX_COMPONENT_IDS,
  getSfxProviderLimits,
  isLocalProvider,
  getLocalProviderInfo,
  findCompatibleLocalAlternative,
} from '../../constants/soundEffects';
import { SoundEffectsAPI } from '../../services/soundEffectsAPI';

// ============================================================================
// Component
// ============================================================================

interface SfxGenerationPanelProps {
  className?: string;
}

export const SfxGenerationPanel = memo(function SfxGenerationPanel({
  className = '',
}: SfxGenerationPanelProps) {
  const { t } = useTranslation();
  const isDesktop = isDesktopApp();
  const {
    installedModels,
    availableModels,
    modelRequirements,
    installProgress,
    installModel,
    stateKnown,
  } = useLocalModelInstalls(SFX_COMPONENT_IDS);

  // Generation lifecycle (isGenerating, progress, audioUrl, error, reset)
  const {
    isGenerating,
    progress,
    audioUrl,
    renewAudioUrl,
    error,
    generateSfx,
    generateSfxFromVideo,
    reset,
  } = useSfxGeneration();

  // Configuration state (form-only, not persisted to job context)
  const [provider, setProvider] = useState<SfxProvider>('stable_audio');
  const [prompt, setPrompt] = useState('');
  const [duration, setDuration] = useState(10);
  const [videoFile, setVideoFile] = useState<File | null>(null);

  // Validation errors (pre-submit, not from generation)
  const [validationError, setValidationError] = useState<string | null>(null);

  // Provider availability from backend
  const [enabledProviders, setEnabledProviders] = useState<Set<string> | null>(null);

  // Fetch provider availability on mount
  useEffect(() => {
    let cancelled = false;
    SoundEffectsAPI.getSfxProviders()
      .then((response) => {
        if (cancelled) return;
        const enabled = new Set<string>();
        for (const p of response.providers) {
          if (p.enabled) {
            enabled.add(p.provider_id);
          }
        }
        setEnabledProviders(enabled);
      })
      .catch(() => {
        // On error, don't restrict - allow all providers
        if (!cancelled) setEnabledProviders(null);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Duration/mode limits for the selected provider. Resolved for cloud AND
  // "_local" ids: the old lookup searched the cloud-only options table, so a
  // local provider fell through as "not V2A" (hiding the video upload for
  // hunyuan_foley_local) and had no max duration at all.
  const providerLimits = getSfxProviderLimits(provider);
  const isV2AProvider = providerLimits.requiresVideo;

  // Auto-select an installed local provider ONCE (free beats paid), then leave
  // the choice alone. installedModels is a fresh Set after every refresh, so
  // without this guard finishing an install would yank the user off the
  // provider they had just picked by hand.
  const autoSelectedRef = useRef(false);
  useEffect(() => {
    if (autoSelectedRef.current || !isDesktop || installedModels.size === 0) return;

    const installedLocalProvider = Object.values(SFX_PROVIDERS).find((value) => {
      if (!isLocalProvider(value)) return false;
      const info = getLocalProviderInfo(value);
      return info !== null && installedModels.has(info.componentId);
    });

    if (installedLocalProvider) {
      autoSelectedRef.current = true;
      setProvider(installedLocalProvider);
    }
  }, [isDesktop, installedModels]);

  // Reset video file when switching away from V2A provider
  useEffect(() => {
    if (!isV2AProvider) {
      setVideoFile(null);
    }
  }, [isV2AProvider]);

  // Adjust duration to provider's max
  useEffect(() => {
    if (duration > providerLimits.maxDuration) {
      setDuration(providerLimits.maxDuration);
    }
  }, [providerLimits.maxDuration, duration]);

  // ------------------------------------------------------------------------
  // Local-model gating — decides whether "Generate" can be offered at all
  // for the selected provider. Driven ENTIRELY by useLocalModelInstalls's real
  // installedModels/modelRequirements (never by parsing a backend error
  // string, see LocalModelInstallGate). This covers both the proactive case
  // (before any click) and the reactive case (a stale error for a provider
  // that is still not installed) with one condition: whenever the gate
  // applies, it replaces the Generate button instead of a guaranteed-fail
  // dead end.
  //
  // `stateKnown` is what keeps "not in installedModels" from being read as
  // "not installed" before /api/v1/components has answered — the Set is empty
  // while the request is in flight and stays empty if it failed, so gating on
  // it alone would offer an installed model a fresh multi-GB download
  // (ltx2_audio_local is 38GB). While the state is unknown the normal flow
  // runs and the backend decides, which is a far cheaper mistake.
  // ------------------------------------------------------------------------
  const selectedLocalInfo = isDesktop ? getLocalProviderInfo(provider) : null;
  const selectedComponentId = selectedLocalInfo?.componentId ?? null;
  const selectedInstalled = selectedComponentId ? installedModels.has(selectedComponentId) : true;
  const selectedRequirements = selectedComponentId
    ? modelRequirements[selectedComponentId]
    : undefined;
  const selectedCompatible = selectedRequirements?.compatible ?? true;
  // `availableModels` is "this backend reported the component at all". Without
  // it, a build that does not ship the model looks identical to one where the
  // model is merely uninstalled, and the gate offers an Install whose POST can
  // only 404 — a dead end wearing a button.
  const selectedExists = selectedComponentId !== null && availableModels.has(selectedComponentId);
  const needsLocalGate = selectedExists && stateKnown && !selectedInstalled;

  const compatibleByComponentId = useMemo(() => {
    const map: Record<string, boolean> = {};
    for (const [componentId, requirements] of Object.entries(modelRequirements)) {
      map[componentId] = requirements.compatible;
    }
    return map;
  }, [modelRequirements]);

  const alternativeProvider = useMemo(() => {
    if (!needsLocalGate || selectedCompatible) return null;
    const altId = findCompatibleLocalAlternative(provider, compatibleByComponentId);
    if (!altId) return null;
    const altInfo = getLocalProviderInfo(altId);
    return altInfo ? { provider: altId, name: altInfo.name } : null;
  }, [needsLocalGate, selectedCompatible, provider, compatibleByComponentId]);

  const handleInstallSelected = useCallback((): void => {
    if (!selectedComponentId) return;
    void installModel(selectedComponentId).catch(() => {
      // Failure is already reflected in installProgress[componentId]
      // (stage: 'error') by the hook itself — nothing else to do here.
    });
  }, [selectedComponentId, installModel]);

  const handleGenerate = useCallback(async () => {
    setValidationError(null);

    if (!prompt.trim() && !isV2AProvider) {
      setValidationError(
        t('soundEffects.errors.promptRequired', { defaultValue: 'Prompt is required' })
      );
      return;
    }

    if (isV2AProvider && !videoFile) {
      setValidationError(
        t('soundEffects.errors.videoRequired', {
          defaultValue: 'Video file is required for V2A generation',
        })
      );
      return;
    }

    if (isV2AProvider && videoFile) {
      await generateSfxFromVideo({
        provider,
        video_file: videoFile,
        prompt: prompt || undefined,
        duration,
      });
    } else {
      await generateSfx({ provider, prompt, duration });
    }
  }, [provider, prompt, duration, videoFile, isV2AProvider, generateSfx, generateSfxFromVideo, t]);

  const handleReset = useCallback(() => {
    setValidationError(null);
    reset();
  }, [reset]);

  const canGenerate = isV2AProvider ? !!videoFile : !!prompt.trim();
  const hasResult = !!audioUrl && !isGenerating;

  // The extension comes from the URL, not a guess. The anchor hardcoded
  // "sound-effect.wav" for every provider, so an MP3 landed on disk named
  // .wav. Also routes desktop saves through the native dialog rather than a
  // raw `<a download>` (which a cross-origin CDN URL ignores outright, making
  // the browser navigate to the audio instead of saving it).
  const sfxExtension = audioUrl?.split('?')[0].split('.').pop()?.toLowerCase() || 'wav';
  const { isDownloading, handleDownload } = useResultDownload(
    audioUrl,
    'sound-effect',
    sfxExtension,
    { renewUrl: renewAudioUrl }
  );

  // Combine validation errors with generation errors for display
  const displayError = validationError ?? error;

  return (
    <div className={`space-y-6 ${className}`}>
      {/* Header - always visible */}
      <div className="flex items-center gap-3 mb-6">
        <div className="w-12 h-12 bg-purple-900/30 rounded-xl flex items-center justify-center">
          <Music className="w-6 h-6 text-purple-400" />
        </div>
        <div>
          <h2 className="text-2xl font-bold text-white">
            {t('soundEffects.title', { defaultValue: 'AI Sound Effects' })}
          </h2>
          <p className="text-sm text-white/50">
            {t('soundEffects.subtitle', {
              defaultValue: 'Generate custom sound effects with AI',
            })}
          </p>
        </div>
      </div>

      {/* Configuration Form - hidden when result is shown */}
      {!hasResult && !isGenerating && (
        <>
          <div className="space-y-6">
            {/* Provider Selector */}
            <SfxProviderSelector
              value={provider}
              onChange={setProvider}
              disabled={isGenerating}
              enabledProviders={enabledProviders}
            />

            {/* V2A: Video Upload OR T2A: Prompt Input */}
            {isV2AProvider ? (
              <SfxVideoUpload
                selectedFile={videoFile}
                onVideoSelected={setVideoFile}
                disabled={isGenerating}
              />
            ) : (
              <SfxPromptInput value={prompt} onChange={setPrompt} disabled={isGenerating} />
            )}

            {/* Duration Slider */}
            <SfxDurationSlider
              value={duration}
              onChange={setDuration}
              provider={provider}
              disabled={isGenerating}
            />
          </div>

          {needsLocalGate ? (
            // The selected provider is either not installed yet or can never
            // run on this machine — offering "Generate" here would be a
            // guaranteed failure (see LocalModelInstallGate for the full state
            // matrix: install / installing / failed / hardware-incompatible).
            <LocalModelInstallGate
              modelName={selectedLocalInfo?.name ?? provider}
              downloadMb={selectedRequirements?.downloadMb ?? 0}
              vramGb={selectedRequirements?.vramGb ?? 0}
              ramGb={selectedRequirements?.ramGb ?? 0}
              diskGb={selectedRequirements?.diskGb ?? 0}
              compatible={selectedCompatible}
              progress={selectedComponentId ? installProgress[selectedComponentId] : undefined}
              onInstall={handleInstallSelected}
              notInstalledDescription={t('soundEffects.localGate.notInstalledDescription', {
                defaultValue: 'Install it once and generate for free on this machine.',
              })}
              incompatibleAction={
                alternativeProvider ? (
                  // AudioCraft and Stable Audio run on Apple Metal; HunyuanFoley
                  // and LTX-2 are CUDA-only. Offer the one that actually works
                  // here instead of a download that never can.
                  <button
                    type="button"
                    onClick={() => setProvider(alternativeProvider.provider)}
                    className={LOCAL_MODEL_GATE_PRIMARY_BUTTON_CLASS}
                  >
                    <Repeat className="w-5 h-5" />
                    <span>
                      {t('soundEffects.localGate.switchButton', {
                        defaultValue: 'Use {{alternative}} instead',
                        alternative: alternativeProvider.name,
                      })}
                    </span>
                  </button>
                ) : (
                  <p className="text-sm text-white/50 text-center px-2">
                    {t('soundEffects.localGate.noAlternative', {
                      defaultValue:
                        'None of the local models are compatible with this machine. Pick a cloud provider above instead.',
                    })}
                  </p>
                )
              }
            />
          ) : (
            <>
              {/* Generate Button */}
              <button
                type="button"
                onClick={handleGenerate}
                disabled={!canGenerate}
                className={`
                  w-full px-6 py-4 rounded-lg font-semibold text-white
                  transition-all min-h-[56px]
                  flex items-center justify-center gap-2
                  ${
                    canGenerate
                      ? 'bg-purple-600 hover:bg-purple-500 active:bg-purple-700'
                      : 'bg-black/30 cursor-not-allowed opacity-50'
                  }
                `}
              >
                <Play className="w-5 h-5" />
                <span>{t('soundEffects.generate', { defaultValue: 'Generate Sound Effect' })}</span>
              </button>

              {/* Error Display */}
              {displayError && (
                <div className="p-4 rounded-lg bg-red-900/20 border border-red-500/30 flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <h4 className="text-sm font-semibold text-red-300 mb-1">
                      {t('soundEffects.error', { defaultValue: 'Error' })}
                    </h4>
                    <p className="text-sm text-red-300">{displayError}</p>
                  </div>
                </div>
              )}

              {/* Info Box */}
              {!displayError && (
                <div className="p-4 rounded-lg bg-blue-900/20 border border-blue-500/30">
                  <div className="flex items-start gap-3">
                    <Sparkles className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
                    <div className="flex-1 text-sm text-blue-300">
                      <p className="font-medium mb-1">
                        {t('soundEffects.infoTitle', { defaultValue: 'Tips for best results:' })}
                      </p>
                      <ul className="list-disc list-inside space-y-1 text-xs text-blue-300/80">
                        <li>
                          {t('soundEffects.tip1', {
                            defaultValue: 'Be specific and descriptive in your prompts',
                          })}
                        </li>
                        <li>
                          {t('soundEffects.tip2', {
                            defaultValue: 'Include details about texture, pitch, and mood',
                          })}
                        </li>
                        <li>
                          {t('soundEffects.tip3', {
                            defaultValue: 'For V2A, ensure video has clear visual action',
                          })}
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </>
      )}

      {/* Loading State */}
      {isGenerating && (
        <div className="text-center py-12">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-2 border-white/30 border-t-purple-500 mb-4" />
          <p className="text-white/60">
            {t('soundEffects.generating', { defaultValue: 'Generating...' })} {progress}%
          </p>
        </div>
      )}

      {/* Result Display - replaces form when complete */}
      {hasResult && (
        <div className="space-y-6">
          <div className="p-4 rounded-lg bg-green-900/20 border border-green-500/30">
            <div className="flex items-start gap-3 mb-3">
              <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <h4 className="text-sm font-semibold text-green-300 mb-1">
                  {t('soundEffects.success', { defaultValue: 'Generation Complete!' })}
                </h4>
                <p className="text-sm text-green-300">
                  {t('soundEffects.successMessage', {
                    defaultValue: 'Your sound effect has been generated successfully.',
                  })}
                </p>
              </div>
            </div>

            {/* Audio Player */}
            <div className="bg-black/30 rounded-lg p-3 border border-white/10">
              <ResultMedia
                kind="audio"
                url={audioUrl}
                renewUrl={renewAudioUrl}
                controls
                className="w-full"
                controlsList="nodownload"
              />
            </div>

            {/* Actions */}
            <div className="mt-3 flex flex-col sm:flex-row gap-2">
              <button
                type="button"
                onClick={handleReset}
                className="flex-1 px-4 py-2 rounded-lg bg-purple-600 hover:bg-purple-500 text-white font-medium transition-all min-h-[44px]"
              >
                {t('soundEffects.generateAnother', { defaultValue: 'Generate Another' })}
              </button>
              <button
                type="button"
                onClick={() => void handleDownload()}
                disabled={isDownloading}
                className="flex-1 px-4 py-2 rounded-lg bg-black/30 hover:bg-black/40 border border-white/10 text-white font-medium transition-all text-center min-h-[44px] flex items-center justify-center disabled:opacity-50"
              >
                {t('soundEffects.download', { defaultValue: 'Download' })}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
});

export default SfxGenerationPanel;
